/**
 * Base URL for the public marketing site (landings, /plan/:id, etc.).
 *
 * Always resolves to the public host (without app. / app-staging.), even when
 * VITE_PUBLIC_SITE_URL is mistakenly set to the app subdomain.
 */

function toPublicHostname(hostname: string): string {
  return hostname
    .replace(/^www\./i, "")
    .replace(/^app-staging\./i, "staging.")
    .replace(/^app\./i, "");
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/$/, "");
}

export function getPublicSiteUrl(
  envUrl: string | undefined = import.meta.env.VITE_PUBLIC_SITE_URL,
  location: Pick<Location, "protocol" | "hostname" | "port"> | undefined =
    typeof window === "undefined" ? undefined : window.location,
): string {
  const configured = envUrl?.trim();

  if (configured) {
    try {
      const url = new URL(configured);
      url.hostname = toPublicHostname(url.hostname);
      const path =
        url.pathname && url.pathname !== "/"
          ? stripTrailingSlash(url.pathname)
          : "";
      return `${url.origin}${path}`;
    } catch {
      // Ignore invalid env values and fall back to the current host.
    }
  }

  if (!location) return "";

  const publicHost = toPublicHostname(location.hostname);
  const portSuffix =
    location.port && location.port !== "80" && location.port !== "443"
      ? `:${location.port}`
      : "";

  return `${location.protocol}//${publicHost}${portSuffix}`;
}

export function getPublicRecoveryPlanUrl(
  planId: string,
  envUrl?: string,
  location?: Pick<Location, "protocol" | "hostname" | "port">,
): string {
  return `${getPublicSiteUrl(envUrl, location)}/plan/${planId}`;
}

export function getPublicClinicBookingUrl(
  slug: string,
  envUrl?: string,
  location?: Pick<Location, "protocol" | "hostname" | "port">,
): string {
  return `${getPublicSiteUrl(envUrl, location)}/p/${slug}`;
}
